import { useQuery } from '@tanstack/react-query';
import { IPatientQuestionnaire, IPatientVASQuestionaire } from './types';
import useDailyQuestionnairesService from './dailyQuestionnairesService';
import { differenceWith, isEqual, groupBy } from 'lodash';

export default function useQuestionnairesService(patientUuid: string) {
  const dailyQuestionnaires  =
    useDailyQuestionnairesService(patientUuid);

  const { data, isLoading } = useQuery<{
    result: IPatientQuestionnaire[];
  }>({
    queryKey: ['patients', patientUuid, 'questionnaires'],
    enabled: !!patientUuid,
  });

  const questionnaires = data?.result ?? [];

  const VASQuestionnaires: IPatientVASQuestionaire[] = questionnaires?.filter((item) =>
    item?.name?.toLocaleLowerCase()?.includes('vas')
  ).map((item) => {
    const groupedQuestions = groupBy(item.questions, 'questionId');

return {
  uuid: item.uuid,
  vasDate: item.createdAt,
  rating: item.score,
  chips: groupedQuestions['symptoms']?.map((item) => (item.rawAnswer || '').replace(/;/g, '; ')),
  comment: (groupedQuestions['comment'] ?? groupedQuestions['comments'])
    ?.map((item) => item.rawAnswer)
    ?.join(' '),
};
  });

  const LEFSQuestionnaires = questionnaires?.filter((item) =>
    item?.name?.toLocaleLowerCase()?.includes('lefs')
  );

  const PSFSQuestionnaires = questionnaires?.filter((item) =>
    item?.name?.toLocaleLowerCase()?.includes('functional scale')
  );

  const FESQuestionnaires = questionnaires?.filter((item) =>
    item?.name?.toLocaleLowerCase()?.includes('falls efficacy scale')
  );

  const PlusMQuestionnaires = questionnaires?.filter((item) =>
    item?.name?.toLocaleLowerCase()?.includes('plusm')
  );

  const AmpProQuestionnaires = questionnaires?.filter((item) =>
    item?.name?.toLocaleLowerCase()?.includes('amppro')
  );

  const generalQuestionnaires = differenceWith(
    questionnaires,
    [
      ...LEFSQuestionnaires,
      ...PSFSQuestionnaires,
      ...FESQuestionnaires,
      ...PlusMQuestionnaires,
      ...AmpProQuestionnaires,
    ],
    isEqual
  );

  return {
    data: questionnaires,
    VASQuestionnaires,
    dailyQuestionnaires,
    LEFSQuestionnaires,
    PSFSQuestionnaires,
    FESQuestionnaires,
    PlusMQuestionnaires,
    AmpProQuestionnaires,
    generalQuestionnaires,
    isLoading: !!patientUuid && isLoading,
  };
}
